import React, { useState } from 'react';
import { Card, Button, Input, Space, Typography, Alert } from 'antd';
import axios from 'axios';
import { buildAPIURL } from '../../config/apiConfig';

const { Title, Text, Paragraph } = Typography;

interface TokenInfo {
  header: any;
  payload: any;
  signature: string;
}

const AuthTest: React.FC = () => {
  const [loginData, setLoginData] = useState({ username: 'admin', password: '' });
  const [testResult, setTestResult] = useState<string>('');
  const [tokenInfo, setTokenInfo] = useState<TokenInfo | null>(null);

  const decodeToken = (token: string): TokenInfo | null => {
    try {
      const parts = token.split('.');
      if (parts.length !== 3) return null;

      const header = JSON.parse(atob(parts[0]));
      const payload = JSON.parse(atob(parts[1]));
      const signature = parts[2];

      return { header, payload, signature };
    } catch (error) {
      return null;
    }
  };

  const checkCurrentToken = () => {
    const token = localStorage.getItem('quanly_truonghoc_token');
    const user = localStorage.getItem('quanly_truonghoc_user');
    
    if (!token) {
      setTestResult('❌ Không có token trong localStorage');
      setTokenInfo(null);
      return;
    }

    const decoded = decodeToken(token);
    if (!decoded) {
      setTestResult('❌ Token không đúng định dạng JWT');
      setTokenInfo(null);
      return;
    }

    setTokenInfo(decoded);
    
    const now = Math.floor(Date.now() / 1000);
    const isExpired = decoded.payload.exp < now;
    
    setTestResult(`
📋 Token info:
- Issued at: ${new Date(decoded.payload.iat * 1000).toLocaleString()}
- Expires at: ${new Date(decoded.payload.exp * 1000).toLocaleString()}
- User: ${decoded.payload.username}
- Role: ${decoded.payload.roleName}
- Status: ${isExpired ? '❌ EXPIRED' : '✅ VALID'}
- User data: ${user ? '✅ EXISTS' : '❌ MISSING'}
    `);
  };

  const testLogin = async () => {
    try {
      setTestResult('🔄 Đang test login...');
      
  const response = await axios.post(buildAPIURL('/auth/login'), loginData);
      
      if (response.data.success) {
        localStorage.setItem('quanly_truonghoc_token', response.data.token);
        localStorage.setItem('quanly_truonghoc_user', JSON.stringify(response.data.user));
        setTestResult('✅ Login thành công! Token mới đã được lưu.');
        checkCurrentToken();
      } else {
        setTestResult(`❌ Login failed: ${response.data.message}`);
      }
    } catch (error: any) {
      setTestResult(`❌ Login error: ${error.response?.data?.message || error.message}`);
    }
  };

  const testAPI = async () => {
    try {
      setTestResult('🔄 Đang test API subjects...');
      
      const token = localStorage.getItem('quanly_truonghoc_token');
  const response = await axios.get(buildAPIURL('/subjects'), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      setTestResult(`✅ API test thành công! Received ${response.data.data?.length || 0} subjects`);
    } catch (error: any) {
      setTestResult(`❌ API test failed: ${error.response?.status} - ${error.response?.data?.message || error.message}`);
    }
  };

  return (
    <div style={{ padding: '20px' }}>
      <Title level={2}>🔍 Authentication Debug</Title>
      
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <Card title="Current Token Status">
          <Button onClick={checkCurrentToken}>Check Current Token</Button>
        </Card>

        <Card title="Test Login">
          <Space>
            <Input
              placeholder="Username"
              value={loginData.username}
              onChange={(e) => setLoginData({ ...loginData, username: e.target.value })}
            />
            <Input.Password
              placeholder="Password"
              value={loginData.password}
              onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
            />
            <Button type="primary" onClick={testLogin}>Login Test</Button>
          </Space>
        </Card>

        <Card title="Test API Call">
          <Button onClick={testAPI}>Test Subjects API</Button>
        </Card>

        {testResult && (
          <Alert
            message="Test Result"
            description={<pre style={{ whiteSpace: 'pre-wrap' }}>{testResult}</pre>}
            type={testResult.includes('✅') ? 'success' : testResult.includes('❌') ? 'error' : 'info'}
          />
        )}

        {tokenInfo && (
          <Card title="Token Details">
            <Paragraph>
              <Text strong>Header:</Text>
              <pre>{JSON.stringify(tokenInfo.header, null, 2)}</pre>
            </Paragraph>
            <Paragraph>
              <Text strong>Payload:</Text>
              <pre>{JSON.stringify(tokenInfo.payload, null, 2)}</pre>
            </Paragraph>
          </Card>
        )}
      </Space>
    </div>
  );
};

export default AuthTest; 